import React, { useEffect, useState, useCallback } from "react";
import "./LeadDetails.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../config/env";

interface TimelineItem {
  id: string;
  type: string;
  agent_name: string | null;
  role_id?: number;
  timeline_time: string;
  otherDetails: {
    source_of_lead?: string;
    client_name: string;
    phone?: string;
    generated_for_project?: string;
    activity?: string;
    remark?: string;
    despositions_name?: string;
    recording_link?: string;
    call_duration?: string;
    meeting_date?: string;
    meeting_time?: string;
    address?: string;
    image?: string;
    developer_name?: string;
    cluster_name?: string;
    project_name?: string;
    date?: string;
    time?: string;
    property_type?: string;
    budget_min?: number;
    budget_max?: number;
    budget_type?: string;
    budget?: string;
    possession_expected?: string;
    requirement?: string;
    [key: string]: any;
  };
  profile_image: string | null;
  time_diff: string;
}

interface LeadData {
  hasPermission: number;
  timeline: TimelineItem[];
  user: {
    name: string;
    role: {
      display_name: string;
    };
  };
  closedLeadDetails: any;
  getLeadRatingData: any;
}

const LeadDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"updated" | "initial">("updated");
  const location = useLocation();

  // Get real-time data from location state
  // const {
  //   raw_lead_id,
  //   customer_address,
  //   raw_leads_buying_location,
  //   raw_leads_property_type,
  //   raw_leads_budget_max,
  //   raw_leads_budget_min,
  //   raw_leads_configuration,
  //   remarks,
  //   bhk,
  //   city
  // } = location.state || {};
  const {
    raw_lead_id,
    raw_leads_remarks,
    customer_address,
    raw_leads_buying_location,
    raw_leads_property_type,
    raw_leads_configuration,
    raw_leads_budget_max,
    raw_leads_budget_min,
    bhk,
    city,
    voice_note,
  } = location.state || {};

  const getRoleDisplayName = useCallback((roleId?: number): string => {
    switch (roleId) {
      case 4:
        return "Sales Agent";
      case 5:
        return "Presales Agent";
      default:
        return "Sales Agent";
    }
  }, []);

  const formatTimelineItem = useCallback(
    (item: TimelineItem) => {
      const { type, timeline_time, otherDetails, time_diff, role_id } = item;
      const roleDisplayName = getRoleDisplayName(role_id);

      switch (type) {
        case "leadgen":
          return {
            icon: "📥",
            title: "Lead Generated",
            description: `Source: ${otherDetails.source_of_lead || "Unknown"}`,
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "system_call":
          return {
            icon: "📞",
            title: "Call Attempted",
            description: "System call was made to client",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "sales_recording_call":
          return {
            icon: "🎙️",
            title: "Sales Recording",
            description: "Sales call recording available",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            hasRecording: !!otherDetails.recording_link,
            recordingUrl: otherDetails.recording_link,
          };
        case "lead":
          return {
            icon: "📋",
            title: "Lead Details Updated",
            description:
              otherDetails.requirement || "Lead requirements updated",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "meeting":
          return {
            icon: "📅",
            title: "Meeting Scheduled",
            description: `Meeting at ${
              otherDetails.address || "Unknown location"
            } on ${otherDetails.meeting_date || ""} ${
              otherDetails.meeting_time || ""
            }`,
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
        case "recording":
          return {
            icon: "🎵",
            title: "Recording Added",
            description: `Call duration: ${
              otherDetails.call_duration || "N/A"
            } seconds`,
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            hasRecording: !!otherDetails.recording_link,
            recordingUrl: otherDetails.recording_link,
          };
        case "disposition":
          return {
            icon: "🏷️",
            title: otherDetails.despositions_name || "Disposition Updated",
            description: otherDetails.remark || "Status updated",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
            hasRecording: !!otherDetails.recording_link,
            recordingUrl: otherDetails.recording_link,
          };
        default:
          return {
            icon: "🔹",
            title: type,
            description: "Activity performed",
            time: timeline_time,
            role: roleDisplayName,
            timeDiff: time_diff,
          };
      }
    },
    [getRoleDisplayName]
  );

  const handleImageClick = useCallback((imageUrl: string | undefined) => {
    if (imageUrl) {
      setSelectedImage(imageUrl);
    }
  }, []);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          alert("Session expired, please login again");
          navigate("/");
          return;
        }

        const res = await axios.get(
          `${API_BASE_URL}/api/leads/timeline/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        setLeadData(res.data);
      } catch (error: any) {
        console.error("Error fetching lead details:", error);

        if (error?.response?.status === 401) {
          localStorage.removeItem("authToken");
          alert("Session expired, please login again");
          navigate("/");
        } else {
          alert("Cannot fetch lead details");
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLeadDetails();
    }
  }, [id, navigate]);

  if (loading) return <p className="loadingText">Loading lead details...</p>;
  if (!leadData) return <p className="loadingText">No lead data found</p>;

  // Filter out activity type timeline items
  const filteredTimeline = leadData.timeline.filter(
    (item) => item.type !== "activity"
  );

  // Get real-time client name from timeline data
  const firstTimelineItem = filteredTimeline[0];
  const clientName =
    firstTimelineItem?.otherDetails?.client_name ||
    leadData.user?.name ||
    "Client";

  // Get real-time status from latest disposition
  const latestDisposition = [...filteredTimeline]
    .reverse()
    .find((item) => item.type === "disposition");
  const currentStatus =
    latestDisposition?.otherDetails?.despositions_name || "New Lead";

  const propertyRequirements = filteredTimeline.filter(
    (item) => item.type === "lead"
  );

  const latestLeadDetails =
    propertyRequirements[propertyRequirements.length - 1]?.otherDetails || {};
  const initialLeadDetails = propertyRequirements[0]?.otherDetails || {};
  const realtimeUpdatedDetails = {
    lead_id: raw_lead_id || latestLeadDetails.lead_id || id || "N/A",
    city: city || latestLeadDetails.city || "N/A",
    buying_location:
      raw_leads_buying_location || latestLeadDetails.buying_location || "N/A",
    budget_range:
      latestLeadDetails.budget_min && latestLeadDetails.budget_max
        ? `₹${latestLeadDetails.budget_min} Lakhs - ₹${latestLeadDetails.budget_max} Lakhs`
        : latestLeadDetails.budget || latestLeadDetails.budget || "N/A",
    bhk:
      bhk || latestLeadDetails.bhk || latestLeadDetails.configuration || "N/A",
    property_type:
      raw_leads_property_type || latestLeadDetails.property_type || "N/A",
    customer_address:
      customer_address || latestLeadDetails.customer_address || "N/A",
  
  };

  // Real-time initial details from first lead data
  const realtimeInitialDetails = {
    lead_id: initialLeadDetails.lead_id || id || "N/A",
    city: initialLeadDetails.city || "N/A",
    buying_location: initialLeadDetails.buying_location || "N/A",
    budget_range:
      initialLeadDetails.budget_min && initialLeadDetails.budget_max
        ? `₹${initialLeadDetails.budget_min} Lakhs - ₹${initialLeadDetails.budget_max} Lakhs`
        : initialLeadDetails.budget || "N/A",
    bhk: initialLeadDetails.bhk || initialLeadDetails.configuration || "N/A",
    property_type: initialLeadDetails.property_type || "N/A",
    customer_address: initialLeadDetails.customer_address || "N/A",
  
  };

  return (
    <div className="leadPage">
      {selectedImage && (
        <div className="imageModal" onClick={() => setSelectedImage(null)}>
          <div className="imageModalContent">
            <span className="closeModal" onClick={() => setSelectedImage(null)}>
              &times;
            </span>
            <img src={selectedImage} alt="Site Visit" className="modalImage" />
          </div>
        </div>
      )}

      <div className="leadDetailsContainer">
        <div className="leadHeader">
          <div className="headerTop" onClick={() => navigate("/dashboard")}>
            ← Back to Leads
          </div>

          <div className="leadProfile">
            <div className="avatar">👤</div>
            <div className="profileInfo">
              <h2>{clientName}</h2>
              <button
                className={`followBtn ${currentStatus
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {currentStatus}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="contentContainer">
        {/* Tab Navigation */}
        <div className="tabsContainer">
          <button
            className={`tabBtn ${activeTab === "updated" ? "active" : ""}`}
            onClick={() => setActiveTab("updated")}
          >
            Updated Details
          </button>
          <button
            className={`tabBtn ${activeTab === "initial" ? "active" : ""}`}
            onClick={() => setActiveTab("initial")}
          >
            Initial Details
          </button>
        </div>

        {/* Updated Details Content - Real-time Data */}

        {activeTab === "updated" && (
          <div className="cardBox">
            <h3>Client Updated Details</h3>
            {propertyRequirements.map((leadItem, index) => (
              <div className="detailsTable">
                <div className="tableRow">
                  <div className="tableLabel">LEAD ID</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.lead_id}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">Project</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.project_name || "N/A"}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">BUYING LOCATION</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.buying_location}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">BUDGET RANGE</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.budget_min &&
                    leadItem.otherDetails.budget_max
                      ? `₹${leadItem.otherDetails.budget_min} - ₹${
                          leadItem.otherDetails.budget_max
                        } ${leadItem.otherDetails.budget_type || "Lakhs"}`
                      : leadItem.otherDetails.budget || "N/A"}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">BHK</div>
                  <div className="tableValue">{raw_leads_configuration}</div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">PROPERTY TYPE</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.property_type || "N/A"}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">CUSTOMER ADDRESS</div>
                  <div className="tableValue">
                    {realtimeInitialDetails.customer_address}
                  </div>
                </div>
                <div className="tableRow">
                  <div className="tableLabel">REMARKS</div>
                  <div className="tableValue">
                    {leadItem.otherDetails.requirement || leadItem.otherDetails.remarks || "N/A"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Initial Details Content - Real-time Data from first lead entry */}

        {activeTab === "initial" && (
          <div className="cardBox">
            <h3>Client Initial Details</h3>
            <div className="detailsTable">
              <div className="tableRow">
                <div className="tableLabel">LEAD ID</div>
                <div className="tableValue">{raw_lead_id}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUYING LOCATION</div>
                <div className="tableValue">{raw_leads_buying_location}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BUDGET RANGE</div>
                <div className="tableValue">
                  ₹ {raw_leads_budget_min} - ₹ {raw_leads_budget_max} Lakhs
                </div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">BHK</div>
                <div className="tableValue">{raw_leads_configuration}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">PROPERTY TYPE</div>
                <div className="tableValue">
                  {realtimeInitialDetails.property_type}
                </div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">CUSTOMER ADDRESS</div>
                <div className="tableValue">{customer_address}</div>
              </div>
              <div className="tableRow">
                <div className="tableLabel">REMARKS</div>
                <div className="tableValue">{"NA"}</div>
              </div>
            </div>
          </div>
        )}
        {/* Timeline Section */}
        <div className="cardBox">
          <h3>Timeline</h3>
          <div className="timelineContainer">
            {filteredTimeline.map((item, index) => {
              const formattedItem = formatTimelineItem(item);
              const isSiteVisit =
                item.type === "disposition" &&
                item.otherDetails.despositions_name === "Site Visit Done";
              const hasRecordingLink =
                item.otherDetails.recording_link &&
                item.otherDetails.recording_link.trim() !== "";

              return (
                <div key={item.id} className="timelineRow">
                  {index !== 0 && <div className="timelineLine"></div>}

                  <div className="timelineIcon">{formattedItem.icon}</div>

                  <div className="timelineContent">
                    <div className="timelineHeader">
                      <h4 className="timelineTitle">{formattedItem.title}</h4>
                      <span className="timelineTime">
                        {new Date(formattedItem.time).toLocaleDateString()} •{" "}
                        {new Date(formattedItem.time).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>

                    <p className="timelineDescription">
                      {formattedItem.description}
                    </p>

                    <p className="timelineAgent">👤 {formattedItem.role}</p>

                    {/* Recording Player for Sales Recording, Recording, or Disposition with recording */}
                    {hasRecordingLink && !isSiteVisit && (
                      <div className="recordingCard">
                        <div className="recordingHeader">
                          <span className="recordingIcon">🎙️</span>
                          <span className="recordingTitle">Call Recording</span>
                        </div>
                        <audio controls className="audioPlayer">
                          <source
                            src={item.otherDetails.recording_link}
                            type="audio/mpeg"
                          />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}

                    {/* Site Visit Details Card */}
                    {isSiteVisit && (
                      <div className="siteVisitInlineCard">
                        <div className="siteVisitGrid">
                          <div className="infoSection">
                            <div className="detailRow">
                              <span className="detailLabel">Developer:</span>
                              <span className="detailValue">
                                {item.otherDetails.developer_name || "N/A"}
                              </span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Cluster:</span>
                              <span className="detailValue">
                                {item.otherDetails.cluster_name || "N/A"}
                              </span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Project Name:</span>
                              <span className="detailValue">
                                {item.otherDetails.project_name || "N/A"}
                              </span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Remarks:</span>
                              <span className="detailValue">
                                {item.otherDetails.remark || "N/A"}
                              </span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">Disposition:</span>
                              <span className="detailValue highlight">
                                {item.otherDetails.despositions_name || "N/A"}
                              </span>
                            </div>
                            <div className="detailRow">
                              <span className="detailLabel">
                                Scheduled Date Time:
                              </span>
                              <span className="detailValue">
                                {item.otherDetails.date &&
                                item.otherDetails.time
                                  ? `${item.otherDetails.date} ${item.otherDetails.time}`
                                  : item.otherDetails.meeting_date &&
                                    item.otherDetails.meeting_time
                                  ? `${item.otherDetails.meeting_date} ${item.otherDetails.meeting_time}`
                                  : "N/A"}
                              </span>
                            </div>

                            {/* Recording Section inside Site Visit */}
                            {item.otherDetails.recording_link && (
                              <div className="recordingInline">
                                <div className="recordingHeaderInline">
                                  <span className="recordingIconInline">
                                    🎙️
                                  </span>
                                  <span className="recordingTitleInline">
                                    Call Recording
                                  </span>
                                </div>
                                <audio controls className="audioPlayerInline">
                                  <source
                                    src={item.otherDetails.recording_link}
                                    type="audio/mpeg"
                                  />
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              </div>
                            )}
                          </div>

                          {/* Right Column - Image */}
                          {item.otherDetails.image && (
                            <div className="imageSection">
                              <div className="imageWrapper">
                                <img
                                  src={item.otherDetails.image}
                                  alt="Site Visit"
                                  className="siteVisitImageInline"
                                  onClick={() =>
                                    handleImageClick(item.otherDetails.image)
                                  }
                                />
                                <div
                                  className="imageOverlayInline"
                                  onClick={() =>
                                    handleImageClick(item.otherDetails.image)
                                  }
                                >
                                  <span className="viewIconInline">🔍</span>
                                  <span>Click to view</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;
